/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCompany } from "@/hooks/useCompany";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Sparkles, Loader2, Download, ExternalLink } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS } from "./types";
import { Badge } from "@/components/ui/badge";

interface ExternalResult {
  source: "unsplash" | "pexels" | "pixabay";
  source_id: string;
  thumb_url: string;
  full_url: string;
  source_url: string;
  attribution: string;
  width: number;
  height: number;
  alt_text: string;
  tags: string[];
}

interface Props {
  onImported: () => void;
  defaultCategory?: string;
}

export default function MediaSourcePanel({ onImported, defaultCategory }: Props) {
  const { toast } = useToast();
  const { currentCompanyId } = useCompany();
  const [query, setQuery] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [category, setCategory] = useState(defaultCategory || "uncategorized");
  const [language, setLanguage] = useState("en");
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState<ExternalResult[]>([]);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setResults([]);
    try {
      const { data, error } = await supabase.functions.invoke("search-external-images", {
        body: { query: query.trim(), per_page: 8, company_id: currentCompanyId },
      });
      if (error) throw error;
      setResults(data.results || []);
      if (!data.results?.length) {
        toast({ title: "No results", description: "Try a different keyword." });
      }
    } catch (e: any) {
      toast({ title: "Search failed", description: e.message, variant: "destructive" });
    } finally {
      setSearching(false);
    }
  };

  const handleImport = async (r: ExternalResult) => {
    setImporting(r.source_id);
    try {
      const { data, error } = await supabase.functions.invoke("import-external-image", {
        body: {
          source: r.source,
          source_id: r.source_id,
          full_url: r.full_url,
          source_url: r.source_url,
          attribution: r.attribution,
          alt_text: r.alt_text,
          tags: r.tags,
          width: r.width,
          height: r.height,
          folder: category,
          language,
          company_id: currentCompanyId,
        },
      });
      if (error) throw error;
      toast({
        title: data.cached ? "Already in library" : "Image imported",
        description: data.cached ? "This image was previously imported." : `Stored in ${CATEGORY_LABELS[category] || category}`,
      });
      onImported();
    } catch (e: any) {
      toast({ title: "Import failed", description: e.message, variant: "destructive" });
    } finally {
      setImporting(null);
    }
  };

  const handleGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-ai-image", {
        body: {
          prompt: aiPrompt.trim(),
          folder: category,
          language,
          alt_text: aiPrompt.trim().slice(0, 200),
          company_id: currentCompanyId,
        },
      });
      if (error) throw error;
      toast({ title: "Image generated", description: `Stored in ${CATEGORY_LABELS[category] || category}` });
      setAiPrompt("");
      onImported();
    } catch (e: any) {
      const msg = e.message?.includes("rate limit") ? "AI rate limit — try again shortly."
        : e.message?.includes("credits") ? "AI credits exhausted — top up your workspace."
        : e.message;
      toast({ title: "Generation failed", description: msg, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-card space-y-4">
      <Tabs defaultValue="external">
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="external">
            <Search className="h-3.5 w-3.5 mr-1.5" />Search Free Stock
          </TabsTrigger>
          <TabsTrigger value="ai">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />Generate with AI
          </TabsTrigger>
        </TabsList>

        {/* Shared destination controls */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <Label className="text-[10px] text-muted-foreground">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} className="text-xs">{CATEGORY_LABELS[c] || c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] text-muted-foreground">Language</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en" className="text-xs">English</SelectItem>
                <SelectItem value="fr" className="text-xs">Français</SelectItem>
                <SelectItem value="ar" className="text-xs">العربية</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <TabsContent value="external" className="space-y-3 mt-4">
          <div className="flex gap-2">
            <Input
              placeholder="Search Unsplash, Pexels & Pixabay..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="h-9 text-xs"
            />
            <Button size="sm" onClick={handleSearch} disabled={searching || !query.trim()}>
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>

          {results.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {results.map((r) => (
                <div key={`${r.source}-${r.source_id}`} className="group relative border rounded-md overflow-hidden bg-muted">
                  <img
                    src={r.thumb_url}
                    alt={r.alt_text}
                    loading="lazy"
                    className="w-full h-24 object-cover"
                  />
                  <Badge variant="secondary" className="absolute top-1 left-1 text-[9px] px-1 h-4 capitalize">
                    {r.source}
                  </Badge>
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1 p-1">
                    <Button
                      size="sm"
                      className="h-6 text-[10px] px-2"
                      onClick={() => handleImport(r)}
                      disabled={importing === r.source_id}
                    >
                      {importing === r.source_id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <><Download className="h-3 w-3 mr-1" />Import</>
                      )}
                    </Button>
                    <a href={r.source_url} target="_blank" rel="noreferrer" className="text-[9px] text-white/80 hover:text-white flex items-center gap-1">
                      <ExternalLink className="h-2.5 w-2.5" />Source
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-[10px] text-muted-foreground">
            Free stock from Unsplash, Pexels & Pixabay. Imported images are cached locally — no repeat downloads.
          </p>
        </TabsContent>

        <TabsContent value="ai" className="space-y-3 mt-4">
          <div className="flex gap-2">
            <Input
              placeholder="Describe the image you want..."
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
              className="h-9 text-xs"
            />
            <Button size="sm" onClick={handleGenerate} disabled={generating || !aiPrompt.trim()}>
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Generated with Gemini. Each generation uses your configured AI provider credits.
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
